/**
 * Scene-change detector — shared helper that flags camera cuts in broadcast
 * footage so the downstream pipeline can reset calibrators (court homography
 * EMA, ball Kalman) instead of blending geometry from two different angles.
 *
 * Algorithm per ingest:
 *   1. Downsample the input to 64x36 grayscale via an OffscreenCanvas.
 *   2. Mean-absolute-difference against the previous downsampled buffer.
 *   3. `isCut = diff > threshold` (default 30 on the 0..255 luma scale).
 *   4. Additionally compute the mean brightness — a sharp drop > 40% of the
 *      previous mean is treated as a "dark" frame (crowd shots, dim close-ups)
 *      and marked as a coarse non-tennis gate via `isDarkDrop`.
 *
 * Works off `ImageData`, `HTMLVideoElement`, or `ImageBitmap`. Downsampling
 * is done on a reusable 64x36 OffscreenCanvas kept inside the detector;
 * falls back to an HTMLCanvasElement when OffscreenCanvas isn't available
 * (jsdom / older Safari).
 *
 * The detector is deliberately framework-agnostic — consumers call
 * `ingest(frame)` from their own RAF loop and react to the return.
 */

export interface SceneChangeResult {
  /** True when the MAD diff exceeds `threshold`. */
  readonly isCut: boolean;
  /** Mean absolute difference on the 0..255 scale. 0 on the first frame. */
  readonly diff: number;
  /** Mean luma of the downsampled frame (0..255). */
  readonly meanLuma: number;
  /**
   * True when the mean luma dropped by more than 40% of the previous frame's
   * mean — a coarse "not tennis play" gate for crowd shots, dim close-ups, and
   * replays that fade in. Consumers can use this to skip pose detection on
   * frames that are unlikely to carry useful body signal.
   */
  readonly isDarkDrop: boolean;
}

export interface SceneChangeOptions {
  /** MAD threshold on 0..255 that flags a cut. Default 30. */
  readonly threshold?: number;
  /** Working width of the downsampled frame. Default 64. */
  readonly workingWidth?: number;
  /** Working height of the downsampled frame. Default 36. */
  readonly workingHeight?: number;
  /** Brightness-drop ratio (0..1) that triggers `isDarkDrop`. Default 0.4. */
  readonly darkDropRatio?: number;
}

const DEFAULTS = {
  threshold: 30,
  workingWidth: 64,
  workingHeight: 36,
  darkDropRatio: 0.4,
} as const;

type AnyCanvas = OffscreenCanvas | HTMLCanvasElement;
type AnyCtx = OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;

function createCanvas(w: number, h: number): AnyCanvas {
  if (typeof OffscreenCanvas !== 'undefined') {
    return new OffscreenCanvas(w, h);
  }
  // jsdom / very old browsers fallback.
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

function getCtx(canvas: AnyCanvas): AnyCtx | null {
  // `willReadFrequently` hints the implementation to keep the bitmap CPU-side,
  // which is exactly what we want for pixel readback every frame.
  // Narrow cast because TS lib.dom types don't agree on the return union.
  return canvas.getContext('2d', { willReadFrequently: true }) as AnyCtx | null;
}

/**
 * Stateful, per-session scene-change detector. Keep one instance alive for
 * the duration of a recording or a video-test playback.
 */
export class SceneChangeDetector {
  private readonly workingWidth: number;
  private readonly workingHeight: number;
  private readonly threshold: number;
  private readonly darkDropRatio: number;

  private canvas: AnyCanvas | null = null;
  private ctx: AnyCtx | null = null;
  private prevLuma: Uint8Array | null = null;
  private prevMean = 0;

  constructor(options: SceneChangeOptions = {}) {
    this.threshold = options.threshold ?? DEFAULTS.threshold;
    this.workingWidth = options.workingWidth ?? DEFAULTS.workingWidth;
    this.workingHeight = options.workingHeight ?? DEFAULTS.workingHeight;
    this.darkDropRatio = options.darkDropRatio ?? DEFAULTS.darkDropRatio;
  }

  /**
   * Ingest a frame and compute the diff vs. the previous frame. The first
   * call always returns `{ isCut: false, diff: 0 }` because there's nothing
   * to compare against yet.
   */
  ingest(
    frame: ImageData | HTMLVideoElement | ImageBitmap | HTMLCanvasElement,
  ): SceneChangeResult {
    const luma = this.downsampleToLuma(frame);
    if (luma === null) {
      return { isCut: false, diff: 0, meanLuma: this.prevMean, isDarkDrop: false };
    }

    // Compute mean + mean-absolute-difference in one pass.
    let sum = 0;
    let diffAcc = 0;
    const n = luma.length;
    const prev = this.prevLuma;
    if (prev !== null && prev.length === n) {
      for (let i = 0; i < n; i++) {
        const v = luma[i]!;
        sum += v;
        diffAcc += Math.abs(v - prev[i]!);
      }
    } else {
      for (let i = 0; i < n; i++) sum += luma[i]!;
    }
    const meanLuma = sum / n;
    const diff = prev ? diffAcc / n : 0;
    const isCut = prev !== null && diff > this.threshold;

    // Dark-drop gate: did the mean luma collapse vs. the previous frame?
    const isDarkDrop =
      prev !== null &&
      this.prevMean > 0 &&
      (this.prevMean - meanLuma) / this.prevMean > this.darkDropRatio;

    this.prevLuma = luma;
    this.prevMean = meanLuma;
    return { isCut, diff, meanLuma, isDarkDrop };
  }

  /** Forget the previous frame so the next ingest returns diff = 0. */
  reset(): void {
    this.prevLuma = null;
    this.prevMean = 0;
  }

  // ---------------------------------------------------------------------------
  // internals
  // ---------------------------------------------------------------------------

  private ensureCanvas(): { canvas: AnyCanvas; ctx: AnyCtx } | null {
    if (this.canvas && this.ctx) return { canvas: this.canvas, ctx: this.ctx };
    const canvas = createCanvas(this.workingWidth, this.workingHeight);
    const ctx = getCtx(canvas);
    if (!ctx) return null;
    this.canvas = canvas;
    this.ctx = ctx;
    return { canvas, ctx };
  }

  /**
   * Draw the input into the 64x36 working canvas and extract a grayscale
   * buffer. Accepts raw `ImageData` (test-friendly path — the luma buffer is
   * computed by bilinear box-nearest sampling instead of canvas drawImage).
   */
  private downsampleToLuma(
    frame: ImageData | HTMLVideoElement | ImageBitmap | HTMLCanvasElement,
  ): Uint8Array | null {
    // Fast-path for ImageData: no DOM required — good for jsdom and workers.
    // Structural check (not `instanceof`) so tests can pass duck-typed
    // ImageData in Node environments where the class isn't defined.
    if (isImageDataLike(frame)) {
      return resampleImageDataToLuma(
        frame,
        this.workingWidth,
        this.workingHeight,
      );
    }

    const handles = this.ensureCanvas();
    if (!handles) return null;
    const { ctx } = handles;
    try {
      // `drawImage` handles <video>, <canvas>, ImageBitmap uniformly.
      // Cast because the union type isn't narrow in our TS lib config.
      (ctx as CanvasRenderingContext2D).drawImage(
        frame as CanvasImageSource,
        0,
        0,
        this.workingWidth,
        this.workingHeight,
      );
    } catch {
      return null;
    }
    let data: ImageData;
    try {
      data = ctx.getImageData(0, 0, this.workingWidth, this.workingHeight);
    } catch {
      return null;
    }
    const out = new Uint8Array(this.workingWidth * this.workingHeight);
    const px = data.data;
    for (let i = 0, j = 0; j < out.length; i += 4, j++) {
      // Rec.601 luma; bit-shift for speed — we don't need perceptual accuracy.
      out[j] = (px[i]! * 299 + px[i + 1]! * 587 + px[i + 2]! * 114 + 500) / 1000 | 0;
    }
    return out;
  }
}

function isImageDataLike(
  x: ImageData | HTMLVideoElement | ImageBitmap | HTMLCanvasElement,
): x is ImageData {
  const candidate = x as { data?: unknown; width?: unknown; height?: unknown };
  return (
    typeof candidate === 'object' &&
    candidate !== null &&
    typeof candidate.width === 'number' &&
    typeof candidate.height === 'number' &&
    candidate.data instanceof Uint8ClampedArray
  );
}

/**
 * Pure function used by the ImageData path (unit-test friendly). Samples the
 * source with nearest-neighbour — the detector doesn't need interpolation,
 * and NN keeps the test math trivial to reason about.
 */
function resampleImageDataToLuma(
  src: ImageData,
  outW: number,
  outH: number,
): Uint8Array {
  const out = new Uint8Array(outW * outH);
  const srcW = src.width;
  const srcH = src.height;
  const px = src.data;
  const sx = srcW / outW;
  const sy = srcH / outH;
  for (let y = 0; y < outH; y++) {
    const srcY = Math.min(srcH - 1, Math.floor(y * sy));
    for (let x = 0; x < outW; x++) {
      const srcX = Math.min(srcW - 1, Math.floor(x * sx));
      const i = (srcY * srcW + srcX) * 4;
      const luma =
        (px[i]! * 299 + px[i + 1]! * 587 + px[i + 2]! * 114 + 500) / 1000;
      out[y * outW + x] = luma | 0;
    }
  }
  return out;
}
